import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Share2 } from 'lucide-react';
import type { GoalSummaryData } from '@/types/goals';
import { cn } from '@/lib/utils';
import { toPng } from 'html-to-image';
import toast from 'react-hot-toast';
import {
  SLIDE_PADDING,
  SLIDE_CARD,
  SLIDE_SCROLL_BOTTOM_PAD,
  STORY_CARD_BG,
  NARRATIVE,
  DUAA_TEXT,
  DUAA_MEANING,
} from './constants';
import { Counter } from './StoryCounter';
import { formatDate } from './utils';

function buildDuaaShareText(): string {
  return [DUAA_TEXT, DUAA_MEANING, '— ramadanlifecenter.com'].join('\n\n');
}

const statCardClass =
  'rounded-2xl border border-primary/20 bg-background/40 p-3 text-center shadow-[0_2px_10px_-4px_hsl(var(--primary)/0.1)] mix-blend-multiply dark:mix-blend-normal sm:p-4';

export function CardClosing({
  summary,
  active,
  className,
}: Readonly<{
  summary: GoalSummaryData;
  active: boolean;
  className?: string;
}>) {
  const navigate = useNavigate();
  const duaaCaptureRef = useRef<HTMLDivElement>(null);
  const [isCopyingImage, setIsCopyingImage] = useState(false);

  const handleCopy = async () => {
    const text = buildDuaaShareText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success('คัดลอกข้อความแล้ว', { position: 'top-center' });
    } catch {
      toast.error('คัดลอกไม่สำเร็จ', { position: 'top-center' });
    }
  };

  const handleWebShare = async () => {
    const text = buildDuaaShareText();
    const navAny = navigator as Navigator & { canShare?: (o: { files?: File[] }) => boolean };

    try {
      if (
        duaaCaptureRef.current &&
        typeof navAny.canShare === 'function' &&
        navAny.canShare({ files: [new File([], 'dummy.png', { type: 'image/png' })] })
      ) {
        setIsCopyingImage(true);
        await document.fonts.ready;
        const dataUrl = await toPng(duaaCaptureRef.current, {
          cacheBust: true,
          pixelRatio: 2,
        });
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const file = new File([blob], 'duaa.png', { type: 'image/png' });
        if (navAny.canShare({ files: [file] })) {
          await navAny.share({
            title: 'ดุอาอ์',
            files: [file],
          });
          setIsCopyingImage(false);
          return;
        }
      }
    } catch {
      // fallback to text share or copy
    } finally {
      setIsCopyingImage(false);
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'ดุอาอ์',
          text,
        });
        return;
      } catch {
        // user cancelled
      }
    }

    await handleCopy();
  };

  const stats = [
    {
      label: 'หน้าอ่านรวม',
      value: summary.total_pages_read,
      targetText: `เป้าหมาย ${summary.total_pages_target.toLocaleString('th-TH')} หน้า`,
      highlight: true,
    },
    {
      label: 'วันที่อ่าน',
      value: summary.active_days,
      targetText: `เป้าหมาย ${summary.total_days_in_goal} วัน`,
    },
    {
      label: 'วันที่พลาด',
      value: summary.missed_days,
      unit: 'วัน',
    },
    {
      label: 'Khatm',
      value: summary.khatm_completed,
      targetText: `เป้าหมาย ${summary.khatm_target}`,
    },
  ];

  return (
    <Card
      className={cn(SLIDE_CARD, 'relative overflow-hidden', STORY_CARD_BG, className)}
    >
      <CardContent
        className={cn(
          'flex min-h-0 flex-1 flex-col justify-start gap-4 overflow-y-auto overscroll-y-contain sm:gap-5 sm:justify-center',
          SLIDE_PADDING,
          SLIDE_SCROLL_BOTTOM_PAD
        )}
      >
        <div className="flex flex-col gap-2 rounded-2xl border border-primary/20 bg-background/40 px-4 py-3 shadow-sm sm:px-5 sm:py-4 mix-blend-multiply dark:mix-blend-normal">
          <h2 className="text-lg font-bold text-foreground sm:text-xl">
            สรุปเส้นทางทั้งหมด
          </h2>
          <p className="text-xs text-muted-foreground">{NARRATIVE.closing}</p>
          <p className="text-xs text-muted-foreground/80">
            {formatDate(summary.goal.start_date)} – {formatDate(summary.goal.end_date)}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 min-w-0 sm:gap-3 sm:grid-cols-3">
          {stats.map(({ label, value, targetText, unit, highlight }) => (
            <div key={label} className={cn(statCardClass)}>
              <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground sm:text-[11px]">
                {label}
              </p>
              <p
                className={cn(
                  'mt-1 text-xl font-bold tabular-nums sm:text-2xl',
                  highlight && 'text-primary'
                )}
              >
                <Counter value={value} active={active} />
                {unit && (
                  <span className="ml-0.5 text-sm font-normal text-muted-foreground">
                    {' '}
                    {unit}
                  </span>
                )}
              </p>
              {targetText && (
                <p className="mt-0.5 text-[10px] text-muted-foreground sm:text-xs">
                  {targetText}
                </p>
              )}
            </div>
          ))}
          <div
            className={cn(
              statCardClass,
              'flex flex-col justify-center col-span-2 sm:col-span-3'
            )}
          >
            <div className="">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs text-muted-foreground">อ่านติดต่อกันมากสุด</span>
                <p className="text-lg font-bold tabular-nums text-orange-500 sm:text-xl">
                  <Counter value={summary.max_streak} active={active} />
                  <span className="ml-2 text-sm font-normal text-muted-foreground ">
                    วัน
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div
            ref={duaaCaptureRef}
            className="rounded-2xl border border-primary/20 bg-background/30 p-5 shadow-[inset_0_2px_8px_hsl(var(--foreground)/0.03)] mix-blend-multiply dark:mix-blend-normal sm:p-6"
          >
            <p
              className="mb-3 text-center text-lg leading-loose text-foreground sm:text-2xl wrap-break-word"
              dir="rtl"
              style={{
                fontFamily: "'Amiri Quran', 'KFGQPC Uthmanic', serif",
              }}
            >
              {DUAA_TEXT}
            </p>
            <p className="text-center text-sm leading-relaxed text-muted-foreground">
              {DUAA_MEANING}
            </p>
          </div>
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={handleWebShare}
              disabled={isCopyingImage}
              className="gap-2 w-full"
            >
              <Share2 className="h-4 w-4" />
              แชร์ดุอาอ์
            </Button>
          </div>
        </div>

        <div className="mx-auto mt-1 flex w-full max-w-md flex-col gap-3 sm:max-w-none">
          <Button
            variant="default"
            className="min-h-11 w-full touch-manipulation shadow-md"
            size="lg"
            onClick={() => navigate('/set-goal', { replace: true })}
          >
            ตั้งเป้าหมายใหม่
          </Button>
          <Button
            variant="secondary"
            className="min-h-11 w-full touch-manipulation"
            size="lg"
            onClick={() => navigate('/feedback?source=goal_completion')}
          >
            feedback เพื่อพัฒนาแอปไปด้วยกัน
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
